import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import {
  User,
  PaperPlaneRight,
  Warning,
  Clock,
  UsersThree,
  CheckCircle,
  XCircle,
  Lightning,
} from "@phosphor-icons/react";
import { useParams } from "react-router-dom";
import { useAppServices } from "../../../hook/services";
import LoadingScreen from "../../../components/LoadingScreen";

const Dashboard = () => {
  const { companyId } = useParams();
  const AppServices = useAppServices();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [appInstalls, setAppInstalls] = useState([]);
  const [appsLogs, setAppsLogs] = useState([]);

  // --- Labels ---
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const { response } = await AppServices.dashboard.crm_genix({
        query: `companyId=${companyId}`,
      });
      if (response) {
        setMessages(response.messageLogs || []);
        setAppInstalls(response.appInstalls || []);
        setAppsLogs(response.appsLogs || []);
      }
    } catch (error) {
      console.error("Failed to fetch logs", error);
    } finally {
      setLoading(false);
    }
  };

  // --- KPI cards ---
  const kpis = [
    {
      title: "Total Installs",
      value: appInstalls.length,
      icon: <UsersThree size={32} weight="duotone" color="#4f46e5" />,
    },
    {
      title: "Active Installs",
      value: appInstalls.filter((i) => i.installStatus === "INSTALL").length,
      icon: <CheckCircle size={32} weight="duotone" color="#16a34a" />,
    },
    {
      title: "UnInstalls",
      value: appInstalls.filter((i) => i.installStatus === "UNINSTALL").length,
      icon: <XCircle size={32} weight="duotone" color="#dc2626" />,
    },
    {
      title: "Total Executions",
      value: appsLogs.length,
      icon: <Lightning size={32} weight="duotone" color="#f59e0b" />,
    },
    {
      title: "Total Messages",
      value: messages.length,
      icon: <PaperPlaneRight size={32} weight="duotone" />,
    },
    {
      title: "Delivered",
      value: messages.filter((m) => m.status?.toLowerCase() === "sent").length,
      icon: <User size={32} weight="duotone" />,
    },
    {
      title: "Failed",
      value: messages.filter((m) => m.status?.toLowerCase() === "failed")
        .length,
      icon: <Warning size={32} weight="duotone" />,
    },
    {
      title: "Pending",
      value: messages.filter((m) => m.status?.toLowerCase() === "pending")
        .length,
      icon: <Clock size={32} weight="duotone" />,
    },
  ];

  // --- Group Helpers ---
  const groupByMonth = (items, getKey, filterFn) => {
    const counts = {};
    items.forEach((item) => {
      const d = new Date(getKey(item));
      const key = monthNames[d.getMonth()];
      if (!counts[key]) counts[key] = 0;
      if (!filterFn || filterFn(item)) counts[key]++;
    });
    return counts;
  };

  const groupByWeekDay = (status) => {
    const counts = {};
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    endOfWeek.setHours(23, 59, 59, 999);

    messages.forEach((msg) => {
      const d = new Date(msg.createdAt);
      if (d >= startOfWeek && d <= endOfWeek) {
        const key = weekDays[d.getDay()];
        if (!counts[key]) counts[key] = 0;
        if (msg.status?.toLowerCase() === status) counts[key]++;
      }
    });
    return counts;
  };

  // --- Messages (monthly + daily) ---
  const deliveredMonthly = groupByMonth(
    messages,
    (m) => m.createdAt,
    (m) => m.status?.toLowerCase() === "sent"
  );
  const failedMonthly = groupByMonth(
    messages,
    (m) => m.createdAt,
    (m) => m.status?.toLowerCase() === "failed"
  );

  const monthlySeries = [
    {
      name: "Delivered",
      data: monthNames.map((m) => deliveredMonthly[m] || 0),
    },
    { name: "Failed", data: monthNames.map((m) => failedMonthly[m] || 0) },
  ];

  const deliveredDaily = groupByWeekDay("sent");
  const failedDaily = groupByWeekDay("failed");

  const dailySeries = [
    { name: "Delivered", data: weekDays.map((d) => deliveredDaily[d] || 0) },
    { name: "Failed", data: weekDays.map((d) => failedDaily[d] || 0) },
  ];

  // --- Installs (monthly) ---
  const installsMonthly = (() => {
    const counts = {};
    appInstalls.forEach((item) => {
      const d = new Date(item.createdAt);
      const key = monthNames[d.getMonth()];
      if (!counts[key]) counts[key] = { install: 0, uninstall: 0 };
      if (item.installStatus === "INSTALL") counts[key].install++;
      if (item.installStatus === "UNINSTALL") counts[key].uninstall++;
    });
    return counts;
  })();

  const installsSeries = [
    {
      name: "Installs",
      data: monthNames.map((m) => installsMonthly[m]?.install || 0),
    },
    {
      name: "Uninstalls",
      data: monthNames.map((m) => installsMonthly[m]?.uninstall || 0),
    },
  ];

  // --- Executions (monthly) ---
  const executionsMonthly = groupByMonth(appsLogs, (log) => log.createdAt);
  const executionsSeries = [
    {
      name: "Executions",
      data: monthNames.map((m) => executionsMonthly[m] || 0),
    },
  ];

  // --- Chart Options Factory ---
  const makeOptions = (categories) => ({
    chart: { toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth" },
    legend: { position: "top" },
    xaxis: { categories },
  });

  // --- Success Rate ---
  const totalDelivered = messages.filter(
    (m) => m.status?.toLowerCase() === "sent"
  ).length;
  const totalFailed = messages.filter(
    (m) => m.status?.toLowerCase() === "failed"
  ).length;
  const totalAttempts = totalDelivered + totalFailed;
  const successRate =
    totalAttempts > 0 ? ((totalDelivered / totalAttempts) * 100).toFixed(1) : 0;

  const successOptions = {
    chart: { type: "donut" },
    labels: ["Delivered", "Failed"],
    legend: { position: "bottom" },
    colors: ["#28a745", "#dc3545"],
  };
  const successSeries = [totalDelivered, totalFailed];
  if (loading) {
    return <LoadingScreen loadingText={"Loading dashboard..."} />;
  }

  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      {/* Loader Overlay */}
      {loading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-white bg-opacity-75"
          style={{ zIndex: 9999 }}
        >
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="row g-4 mb-4">
        {kpis.map((kpi, idx) => (
          <div className="col-12 col-sm-6 col-lg-3" key={idx}>
            <div className="card shadow-sm border-0 h-100 p-3 d-flex flex-row align-items-center">
              <div className="text-primary me-3">{kpi.icon}</div>
              <div>
                <h6 className="text-muted mb-1">{kpi.title}</h6>
                <h4 className="fw-bold mb-0">{kpi.value}</h4>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="row g-4 mb-4">
        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">App Installs Overview (Monthly)</h6>
            <Chart
              options={makeOptions(monthNames)}
              series={installsSeries}
              type="bar"
              height={280}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Action Executions (Monthly)</h6>
            <Chart
              options={makeOptions(monthNames)}
              series={executionsSeries}
              type="area"
              height={280}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Message Overview (Daily)</h6>
            <Chart
              options={makeOptions(weekDays)}
              series={dailySeries}
              type="bar"
              height={280}
            />
          </div>
        </div>

        <div className="col-12 col-lg-6">
          <div className="card shadow-sm border-0 p-3 h-100 text-center">
            <h6 className="fw-bold mb-3">Success Rate</h6>
            <Chart
              options={successOptions}
              series={successSeries}
              type="donut"
              height={280}
            />
            <h4 className="fw-bold mt-3 text-success">{successRate}%</h4>
            <p className="text-muted small">Overall delivery success</p>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0 p-3 h-100">
            <h6 className="fw-bold mb-3">Message Overview (Monthly)</h6>
            <Chart
              options={makeOptions(monthNames)}
              series={monthlySeries}
              type="line"
              height={280}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
